import { Component } from '@theme/component';

const COPY_CONFIRMATION_TIMEOUT_MS = 2000;

/**
 * A custom element that shares the current page via the native share sheet,
 * falling back to copying the link to the clipboard.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} shareConfirmation - The "Link copied" confirmation element.
 * @extends Component<Refs>
 */
class ProductShareButton extends Component {
  requiredRefs = ['shareConfirmation'];

  /** @type {number | undefined} */
  #hideTimeout;

  get productId() {
    return this.dataset.productId ?? '';
  }

  get productTitle() {
    return this.dataset.productTitle ?? '';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#hideTimeout);
  }

  /**
   * @param {Event} event
   */
  share(event) {
    event.preventDefault();
    event.stopPropagation();

    const url = window.location.href;

    if (navigator.share) {
      navigator
        .share({ title: this.productTitle, url })
        .then(() => this.#dispatchShareEvent('native'))
        .catch((/** @type {DOMException} */ error) => {
          // A cancelled share sheet also rejects the promise; only fall back
          // to the clipboard for genuine failures, not user cancellation.
          if (error?.name !== 'AbortError') this.#copyFallback(url);
        });
      return;
    }

    this.#copyFallback(url);
  }

  /**
   * @param {string} url
   */
  #copyFallback(url) {
    navigator.clipboard.writeText(url).then(() => {
      this.#dispatchShareEvent('clipboard');
      this.#showConfirmation();
    });
  }

  #showConfirmation() {
    const { shareConfirmation } = this.refs;

    clearTimeout(this.#hideTimeout);
    shareConfirmation.classList.remove('hidden');

    this.#hideTimeout = setTimeout(() => {
      shareConfirmation.classList.add('hidden');
    }, COPY_CONFIRMATION_TIMEOUT_MS);
  }

  /**
   * @param {'native' | 'clipboard'} method
   */
  #dispatchShareEvent(method) {
    this.dispatchEvent(
      new CustomEvent('share:clicked', {
        bubbles: true,
        detail: { productId: this.productId, method },
      })
    );
  }
}

if (!customElements.get('product-share-button')) {
  customElements.define('product-share-button', ProductShareButton);
}
